import * as React from "react";
import WcaTable from "../components/WcaTable";
import { loadWindsForTerminals } from "../services/windsService";
import WeatherLinks from "../components/weatherLinks";
import ForecastTable from "../components/ForecastTable";
import { computeWcaLegs } from "../services/wcaService";
import useWcaPersistence from "../hooks/useWcaPersistence";
import { fetchWindTempStations } from "../components/windsAloft";
import { Link } from "react-router-dom";


import {
  defaultWcaRows,
  defaultForecastTerminals,
} from "../utils/wcaDefaults";

const bg = "/P1010053.jpg";

const WcaPage = () => {
  
  // ------------------------------------------------------------
  // STATE HOOKS declaration
  // ------------------------------------------------------------
  // Default values used when creating row

  const [initialized, setInitialized] = React.useState(false);
  const [windsRegion, setWindsRegion] = React.useState("mia");
  const [windsFcst, setWindsFcst] = React.useState("6");
  const [wcaRows, setWcaRows] = React.useState(defaultWcaRows);
  const [forecastTerminals, setForecastTerminals] = React.useState(defaultForecastTerminals);
  const [stationsData, setStationsData] = React.useState(null);

  const [altitudes, setAltitudes] = React.useState({
        alt1: 3000,
        alt2: 6000,
        alt3: 9000,
      });




  React.useEffect(() => {
        fetchWindTempStations().then(setStationsData);
      }, []);

  const updateWcaCell = (i, field, value) => {  // state update function for WCA table cell
    setWcaRows((prev) => {   // update react state to previous state
      const copy = [...prev]; // react states are immutable so create a copy first
      copy[i] = { ...copy[i], [field]: value };  // copies existing row object and updates
      return copy;
    });
  };
 
    const updateForecastTerminalCell = (i, field, value) => {
       setForecastTerminals((prev) => {
          const copy = [...prev];  // create shallow copy of state array
          copy[i] = { ...copy[i], [field]: value };  // update specific field
          return copy;
      });
    };
    const stationList = React.useMemo(() => {
      return stationsData?.regions?.[windsRegion] || [];
        }, [stationsData, windsRegion]);

  /**
   * ------------------------------------------------------------
   * Load winds aloft for all forecast terminal rows
   * ------------------------------------------------------------
   */
    const loadAllWinds = async () => {
    const updated = await loadWindsForTerminals(
      windsRegion,
      windsFcst,
      forecastTerminals,
      altitudes
    );

    setForecastTerminals(updated);
  };




  /**
   * *****************************************
   * PERSIST departure and wind values
   * 'navlog_globals_v1' is the key value used in LocalStorage
   * to store the global variables as a JSON string.  
   * When the component mounts, it retrieves the saved values 
   * and updates the state accordingly.
   * 
   * 'g' is an object that contains all the global variables
   * ***************************************
   */
      useWcaPersistence({
          forecastTerminals,
          setForecastTerminals,
          windsRegion,
          setWindsRegion,
          windsFcst,
          setWindsFcst,
          setInitialized,
        });

  
    // ------------------------------------------------------------
    // AUTO LOAD FT station list
    // ------------------------------------------------------------
      React.useEffect(() => {
        if (!stationList.length) return;

        setForecastTerminals((prev) =>
          prev.map((row, i) => ({
            ...row,
            ft: stationList[i] || stationList[0] || "",
          }))
        );
        // don't use stationList instead of windsRegion.. it causes an infinite loop.
      }, [windsRegion]);  
    // ------------------------------------------------------------
    // AUTO LOAD WINDS when selected FT rows change
    // ------------------------------------------------------------
    React.useEffect(() => {
      if (!initialized) return; // only auto load after initial load from LocalStorage
      console.log("**** FT changed, reload winds:", forecastTerminals.map((r) => r.ft));
      
      loadAllWinds();
    }, [
      initialized,
      forecastTerminals[0]?.ft, 
      forecastTerminals[1]?.ft,
      altitudes.alt1,
      altitudes.alt2,
      altitudes.alt3,
    ]);

    // ------------------------------------------------------------
    // MATCH WCA rows to selected Forecast Terminal rows
    // Forecast row 0 FT -> WCA row 0 FT
    // Forecast row 1 FT -> WCA row 1 FT
    // ------------------------------------------------------------
    React.useEffect(() => {
        setWcaRows((prev) =>
          prev.map((row, i) => ({
            ...row,
            ft: forecastTerminals[i]?.ft || row.ft,
          }))
        );
      }, [
        forecastTerminals[0]?.ft,
        forecastTerminals[1]?.ft,
      ]);


  /**
   * ------------------------------------------------------------
   * COMPUTE navlog outputs per row
   * 
   * memoize the computed navlog legs so that they are only recalculated
   * when the input values (navlogRows and forecastTerminals) change.
   * 
   * note: map creates a new array
   * for each row in the navlog, it finds the corresponding forecast terminal data
   *  ------------------------------------------------------------
   */
  
  const wcaLegsComputed = React.useMemo(() => {
  return computeWcaLegs(wcaRows, forecastTerminals, altitudes);
}, [wcaRows, forecastTerminals, altitudes]);


  // ------------------------------------------------------------
  // User Interface HTML
  // ------------------------------------------------------------
  return (
        <article className="App-article" style={{minHeight: "100vh"}}>

     {/* ============================================================
        HERO SECTION
    ============================================================ */}
    <section className="tool-hero">
      <h1>Winds Aloft & Wind Correction Angle Calculator</h1>

      <p style={{ fontSize: "18px" }}>
        Calculate wind correction angle (WCA), ground speed, and true heading
        using forecast winds aloft data. This tool helps pilots build accurate
        navigation logs for enroute flight planning.
      </p>
    </section>

    {/* ============================================================
        TOP AD
    ============================================================ */}
    <div className="ad-banner">Ad Space</div>

    {/* ============================================================
            Tool section
        ============================================================ */}

    <div 
      style={{ padding: "12px"}}>


      <h3 className="wca-text-grid">
        Winds and Temperatures Aloft at the selected Forecast-Terminal (FT)
      </h3>
  
      <hr />

      {/* Controls */}
      <div 
        style={{
          display: "flex", 
          gap: "18px", 
          alignItems: "center", 
          marginTop: "12px", 
          marginBottom: "8px" 
          }}>
      <div>
        <label style={{ marginRight: "8px", fontWeight: "bold" }}>Region</label>
        <select value={windsRegion} onChange={(e) => setWindsRegion(e.target.value)}>
          <option value="mia">Southeast</option>
          <option value="bos">Northeast</option>
          <option value="chi">Northcentral</option>
          <option value="dfw">Southcentral</option>
          <option value="slc">Rocky Mountains</option>
          <option value="sfo">Pacific Coast</option>
  
        </select>
      </div>

      <div>
        <label style={{ marginRight: "8px", fontWeight: "bold" }}>Forecast Hours</label>
        <select value={windsFcst} onChange={(e) => setWindsFcst(e.target.value)}>
          <option value="6">6</option>
          <option value="12">12</option>
          <option value="24">24</option>
        </select>
        </div>

        <div>
          <label style={{ marginRight: "8px", fontWeight: "bold" }}>Level</label>
          <span>low</span>
        </div>
      </div>

            {/* Forecast Table Wind selections */}
            <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <strong>Winds Aloft Altitudes:</strong>

        <input
          type="number"
          value={altitudes.alt1}
          onChange={(e) =>
            setAltitudes({ ...altitudes, alt1: Number(e.target.value) })
          }
          style={{ width: "80px", marginLeft: "10px" }}
        />

        <input
          type="number"
          value={altitudes.alt2}
          onChange={(e) =>
            setAltitudes({ ...altitudes, alt2: Number(e.target.value) })
          }
          style={{ width: "80px", marginLeft: "10px" }}
        />

        <input
          type="number"
          value={altitudes.alt3}
          onChange={(e) =>
            setAltitudes({ ...altitudes, alt3: Number(e.target.value) })
          }
          style={{ width: "80px", marginLeft: "10px" }}
        />
      </div>

      {/* Forecast Table */}
      <div className= "tool-card-forecast-table">
      <ForecastTable
        forecastTerminals={forecastTerminals}
        stationList={stationList}
        updateForecastTerminalCell={updateForecastTerminalCell}
        altitudes={altitudes}
      />
      </div> 
      
      
      {/* WCA Table */}
        <div className= "tool-card-wca-table">
          <WcaTable
            wca={wcaLegsComputed}
            onEdit={updateWcaCell}
          />
          </div>

      {/* Weather Links */}
      <div className="wca-total-container">
      <WeatherLinks />
        </div>
      </div>

      {/* ============================================================
              SECOND AD 
          ============================================================ */}
    <div className="ad-banner">Ad Space</div>


    {/* ============================================================
        SEO CONTENT SECTION
    ============================================================ */}
    <div className="tool-content">

      <h2>What are Winds Aloft?</h2>
      <p>
        Winds aloft forecasts provide wind direction, wind speed, and temperature
        at various altitudes. Pilots use this information to estimate conditions
        during enroute flight and to calculate wind effects on aircraft performance.
      </p>

      <h2>What is Wind Correction Angle (WCA)?</h2>
      <p>
        Wind correction angle (WCA) is the angle a pilot must adjust the aircraft's
        heading to compensate for crosswind drift. Without this correction, the
        aircraft will be pushed off course by the wind.
      </p>

      <h2>How to Calculate Wind Correction</h2>
      <p>
        Wind correction is calculated using wind direction, wind speed, true
        airspeed, and desired true course. The wind's effect is resolved into crosswind
        and headwind components to determine the required heading and ground speed.
      </p>

      <h2>Why It Matters</h2>
      <p>
        Accurate wind correction ensures the aircraft remains on course and arrives
        at each checkpoint as planned. Poor wind correction can result in navigation 
        errors and increased fuel consumption.
      </p>

    </div>

    {/* ============================================================
        RELATED TOOLS
    ============================================================ */}
    <section className="related-tools-section">
      <h2>Related Tools</h2>

      <p>
        <Link to="/density-altitude">Density Altitude</Link> |
        <Link to="/cw">Crosswind</Link> |
        <Link to="/wtbals">Weight & Balance</Link>
      </p>
    </section>
     </article>
  );
};

export default WcaPage;